import parse, {
  type DOMNode,
  type HTMLReactParserOptions,
  attributesToProps,
  domToReact,
  Element,
} from "html-react-parser";
import React from "react";

interface ICommonStringParserProps {
  dangerHtml?: string;
}

/**
 * The tags rich text may use: what the admin editor (policies, about) and hosts'
 * descriptions write. Any other tag is unwrapped to its children.
 */
const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "div",
  "span",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "small",
  "sub",
  "sup",
  "ul",
  "ol",
  "li",
  "a",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "hr",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
]);
/** Dropped with everything inside: they run code, load things or submit somewhere. */
const DROPPED_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "meta",
  "link",
  "base",
  "svg",
  "math",
  "template",
  "noscript",
]);
/** Attributes kept; no event handlers, no `src`, no `class` or `id` to hijack styles. */
const ALLOWED_ATTRIBUTES = new Set(["start", "dir", "colspan", "rowspan"]);
/**
 * The only inline styles kept: what the editors use for text. Anything that positions or
 * sizes would let a host's description cover the page with its own link or fake panel.
 */
const ALLOWED_STYLES = new Set([
  "color",
  "background-color",
  "text-align",
  "font-weight",
  "font-style",
  "text-decoration",
]);
const SAFE_HREF = /^(https?:|mailto:|tel:|\/|#)/i;

const safeStyle = (style: string | undefined): string | undefined => {
  const kept = (style ?? "").split(";").flatMap((declaration) => {
    const colon = declaration.indexOf(":");
    const property = declaration.slice(0, colon).trim().toLowerCase();
    const value = declaration.slice(colon + 1).trim();
    return colon > 0 &&
      ALLOWED_STYLES.has(property) &&
      /^[\w\s#%.,()-]+$/.test(value)
      ? [`${property}:${value}`]
      : [];
  });
  return kept.length > 0 ? kept.join(";") : undefined;
};

const parserOptions: HTMLReactParserOptions = {
  replace(node) {
    if (!(node instanceof Element)) return undefined;
    if (DROPPED_TAGS.has(node.name)) return <></>;
    const children = domToReact(node.children as DOMNode[], parserOptions);
    if (!ALLOWED_TAGS.has(node.name)) return <>{children}</>;

    const attributes: Record<string, string> = {};
    for (const [name, value] of Object.entries(node.attribs)) {
      if (ALLOWED_ATTRIBUTES.has(name)) attributes[name] = value;
    }
    const style = safeStyle(node.attribs.style);
    if (style) attributes.style = style;
    if (node.name === "a") {
      const href = node.attribs.href?.trim() ?? "";
      if (!SAFE_HREF.test(href)) return <>{children}</>;
      attributes.href = href;
      // External links open apart, with no access back to this page.
      if (/^https?:/i.test(href)) {
        attributes.target = "_blank";
        attributes.rel = "noopener noreferrer";
      }
    }
    const props = attributesToProps(attributes);
    return node.name === "br" || node.name === "hr"
      ? React.createElement(node.name, props)
      : React.createElement(node.name, props, children);
  },
};

/**
 * Renders HTML from the admin editor or a host's description, sanitized: only the
 * rich-text tags and a few harmless attributes survive, links only to http(s), mail,
 * phone or this site. It is server-rendered, so an unsanitized `<script>` would run.
 */
const CommonStringParser: React.FC<ICommonStringParserProps> = ({
  dangerHtml,
}) => {
  return <>{parse(dangerHtml || "", parserOptions)}</>;
};

export default CommonStringParser;
